function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { useState, useRef, useCallback, useEffect } from 'react';
import { debounce } from '../../../utilities/debounce.js';
var DEBOUNCE_PERIOD = 250;
var SELECT_ALL_ACTIONS_HEIGHT = 41;
var PAGINATION_WIDTH_OFFSET = 64;
var SCROLL_BAR_HEIGHT = 13;
var INDEX_TABLE_INITIAL_OFFSET = 32;
var RESOURCE_LIST_INITIAL_OFFSET = 48;
function useIsSelectAllActionsSticky(_ref) {
  var selectMode = _ref.selectMode,
    hasPagination = _ref.hasPagination,
    tableType = _ref.tableType;
  var hasIOSupport = typeof window !== 'undefined' && Boolean(window.IntersectionObserver);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isSelectAllActionsSticky = _useState2[0],
    setIsSticky = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isScrolledPastTop = _useState4[0],
    setIsScrolledPastTop = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    selectAllActionsAbsoluteOffset = _useState6[0],
    setSelectAllActionsAbsoluteOffset = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    selectAllActionsMaxWidth = _useState8[0],
    setSelectAllActionsMaxWidth = _useState8[1];
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    selectAllActionsOffsetLeft = _useState10[0],
    setSelectAllActionsOffsetLeft = _useState10[1];
  var selectAllActionsIntersectionRef = useRef(null);
  var tableMeasurerRef = useRef(null);
  var widthOffset = hasPagination ? PAGINATION_WIDTH_OFFSET : 0;
  var initialPostOffset = tableType === 'index-table' ? INDEX_TABLE_INITIAL_OFFSET + SCROLL_BAR_HEIGHT : RESOURCE_LIST_INITIAL_OFFSET;
  var postScrollOffset = initialPostOffset + SELECT_ALL_ACTIONS_HEIGHT;
  var handleIntersect = function handleIntersect(entries) {
    entries.forEach(function (entry) {
      setIsSticky(!entry.isIntersecting);
    });
  };
  var handleTableIntersect = function handleTableIntersect(entries) {
    entries.forEach(function (entry) {
      var _entry$rootBounds;
      var isScrolledPastTop = entry.boundingClientRect.top > 0 && !entry.isIntersecting;
      var rootBoundsHeight = ((_entry$rootBounds = entry.rootBounds) === null || _entry$rootBounds === void 0 ? void 0 : _entry$rootBounds.height) || 0;
      var hasTableOffscreen = entry.boundingClientRect.top + entry.boundingClientRect.height > rootBoundsHeight;
      if (hasTableOffscreen) {
        setIsSticky(entry.isIntersecting);
      }
      setIsScrolledPastTop(isScrolledPastTop);
    });
  };
  var options = {
    root: null,
    rootMargin: '0px',
    threshold: 0
  };
  var observerRef = useRef(hasIOSupport ? new IntersectionObserver(handleIntersect, options) : null);
  var tableOptions = {
    root: null,
    rootMargin: "0px 0px -".concat(postScrollOffset, "px 0px"),
    threshold: 0
  };
  var tableObserverRef = useRef(hasIOSupport ? new IntersectionObserver(handleTableIntersect, tableOptions) : null);
  var computeTableDimensions = useCallback(function () {
    var node = tableMeasurerRef.current;
    if (!node) {
      return {
        maxWidth: 0,
        offsetHeight: 0,
        offsetLeft: 0
      };
    }
    var box = node.getBoundingClientRect();
    var paddingHeight = selectMode ? SELECT_ALL_ACTIONS_HEIGHT : 0;
    var offsetHeight = box.height - paddingHeight;
    var maxWidth = box.width - widthOffset;
    var offsetLeft = box.left;
    setSelectAllActionsAbsoluteOffset(offsetHeight);
    setSelectAllActionsMaxWidth(maxWidth);
    setSelectAllActionsOffsetLeft(offsetLeft);
  }, [selectMode, widthOffset]);
  var computeDimensionsPastScroll = useCallback(function () {
    setSelectAllActionsAbsoluteOffset(initialPostOffset);
  }, [initialPostOffset]);
  useEffect(function () {
    if (isScrolledPastTop) {
      computeDimensionsPastScroll();
    } else {
      computeTableDimensions();
    }
    var debouncedComputeTableHeight = debounce(computeTableDimensions, DEBOUNCE_PERIOD, {
      trailing: true
    });
    window.addEventListener('resize', debouncedComputeTableHeight);
    return function () {
      return window.removeEventListener('resize', debouncedComputeTableHeight);
    };
  }, [isScrolledPastTop, computeDimensionsPastScroll, computeTableDimensions]);
  useEffect(function () {
    var observer = observerRef.current;
    var tableObserver = tableObserverRef.current;
    if (!observer || !tableObserver) {
      return;
    }
    var node = selectAllActionsIntersectionRef.current;
    var tableNode = tableMeasurerRef.current;
    if (node) {
      observer.observe(node);
    }
    if (tableNode) {
      tableObserver.observe(tableNode);
    }
    return function () {
      observer === null || observer === void 0 || observer.disconnect();
      tableObserver === null || tableObserver === void 0 || tableObserver.disconnect();
    };
  }, [selectAllActionsIntersectionRef]);
  return {
    selectAllActionsIntersectionRef: selectAllActionsIntersectionRef,
    tableMeasurerRef: tableMeasurerRef,
    isSelectAllActionsSticky: isSelectAllActionsSticky,
    selectAllActionsAbsoluteOffset: selectAllActionsAbsoluteOffset,
    selectAllActionsMaxWidth: selectAllActionsMaxWidth,
    selectAllActionsOffsetLeft: selectAllActionsOffsetLeft,
    computeTableDimensions: computeTableDimensions,
    isScrolledPastTop: isScrolledPastTop,
    selectAllActionsPastTopOffset: initialPostOffset,
    scrollbarPastTopOffset: initialPostOffset - SCROLL_BAR_HEIGHT
  };
}
export { useIsSelectAllActionsSticky };